import { checkHashMapMatch } from './check-hashmap-match';

/**
 * Checks if two objects match deeply using a specified strategy.
 *
 * @template T - The type of the first object.
 * @template U - The type of the second object.
 * @param {T} obj - The first object to compare.
 * @param {U} target - The second object to compare.
 * @param {(obj: T, target: U) => boolean} [strategy=checkHashMapMatch] - The strategy function to use for comparison.
 * The default strategy is `checkHashMapMatch`, which is less efficient but returns true for objects with different field orders.
 * Another possible strategy is `checkStringifyMatch`, which will return false for objects with different field orders.
 * @returns {boolean} - Returns true if the objects match according to the specified strategy, otherwise false.
 * @example
 * const obj5 = { a: 1, b: { c: 2 } };
 * const obj6 = { b: { c: 2 }, a: 1 };
 * console.log(checkDeepMatch(obj5, obj6)); // true with default strategy
 *
 * @example
 * const obj7 = { a: 1, b: { c: 2 } };
 * const obj8 = { b: { c: 2 }, a: 1 };
 * console.log(checkDeepMatch(obj7, obj8, checkStringifyMatch)); // false with checkStringifyMatch strategy
 */
export function checkDeepMatch<T, U>(
	obj: T,
	target: U,
	strategy: (obj: T, target: U) => boolean = checkHashMapMatch,
): boolean {
	return strategy(obj, target);
}
